import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import TimerDisplay from '../atoms/TimerDisplay';
import TimerEffect, { EffectType } from '../atoms/TimerEffect';
import Button from '../atoms/Button';
import TimerForm from './TimerForm';
import { FaPlay, FaPause, FaStop, FaRedo, FaEdit, FaTrash, FaExpand, FaCompress } from 'react-icons/fa';

export type TimerMode = 'countdown' | 'stopwatch';

export interface TimerCardProps {
  id: string;
  name: string;
  initialHours?: number;
  initialMinutes?: number;
  initialSeconds?: number;
  mode?: TimerMode;
  showMilliseconds?: boolean;
  effectType?: EffectType;
  effectOpacity?: number;
  effectColor?: string;
  effectSecondaryColor?: string;
  effectSpeed?: number;
  onComplete?: () => void;
  onDelete?: (id: string, confirmed?: boolean) => void;
  onEdit?: (id: string, data: {
    name: string;
    hours: number;
    minutes: number;
    seconds: number;
    mode: TimerMode;
    showMilliseconds: boolean;
    effectType: EffectType;
    effectOpacity: number;
    effectColor?: string;
    effectSecondaryColor?: string;
    effectSpeed: number;
  }) => void;
  className?: string;
}

const TimerCard: React.FC<TimerCardProps> = ({
  id,
  name,
  initialHours = 0,
  initialMinutes = 0,
  initialSeconds = 0,
  mode = 'countdown',
  showMilliseconds = false,
  effectType = 'circle',
  effectOpacity = 0.8,
  effectColor,
  effectSecondaryColor,
  effectSpeed = 1,
  onComplete,
  onDelete,
  onEdit,
  className = '',
}) => {
  // 国际化
  const { t } = useTranslation();
  
  // 从Redux获取设置
  const { notificationSound, notificationsEnabled } = useSelector((state: RootState) => state.settings);
  
  // 计算初始总时间（毫秒）
  const initialTotalMs = (initialHours * 3600 + initialMinutes * 60 + initialSeconds) * 1000;
  
  // 状态
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [remainingMs, setRemainingMs] = useState(initialTotalMs);
  const [isEditing, setIsEditing] = useState(false);
  
  // 删除确认状态
  const [deleteConfirmActive, setDeleteConfirmActive] = useState(false);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  
  // 引用
  const animationFrameRef = useRef<number | null>(null);
  const lastTickTimeRef = useRef<number | null>(null);
  const isRunningRef = useRef(isRunning);
  const remainingMsRef = useRef(remainingMs);
  const elapsedMsRef = useRef(elapsedMs);
  
  // 添加全屏状态
  const [isFullscreen, setIsFullscreen] = useState(false);
  const timerCardRef = useRef<HTMLDivElement>(null);
  
  // 创建一个标记初始渲染的引用
  const initialRenderRef = useRef(true);
  // 创建一个标记模式是否变化的引用
  const prevModeRef = useRef(mode);
  
  // 只在组件初始化和模式变化时重置计时器状态
  useEffect(() => {
    // 只在以下情况重置计时器：
    // 1. 组件第一次渲染
    // 2. 模式发生变化
    // 3. 初始时间变化且计时器未运行
    const modeChanged = prevModeRef.current !== mode;
    prevModeRef.current = mode;
    
    if (initialRenderRef.current || modeChanged || (!isRunning && prevIsRunningRef.current !== isRunning)) {
      // 只有当计时器未运行时，才根据模式重置状态
      if (!isRunning) {
        if (mode === 'countdown') {
          setRemainingMs(initialTotalMs);
          remainingMsRef.current = initialTotalMs;
        } else if (mode === 'stopwatch') {
          setElapsedMs(0);
          elapsedMsRef.current = 0;
        }
      }
      
      // 更新初始渲染标记
      initialRenderRef.current = false;
    }
  }, [initialTotalMs, mode]);
  
  // 添加引用以跟踪之前的isRunning状态，便于识别首次启动
  const prevIsRunningRef = useRef(isRunning);
  useEffect(() => {
    // 更新之前的运行状态
    prevIsRunningRef.current = isRunning;
  }, [isRunning]);
  
  // 更新refs以反映最新状态
  useEffect(() => {
    isRunningRef.current = isRunning;
    remainingMsRef.current = remainingMs;
    elapsedMsRef.current = elapsedMs;
  }, [isRunning, remainingMs, elapsedMs]);
  
  // 点击外部区域关闭删除确认
  useEffect(() => {
    if (!deleteConfirmActive) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      if (deleteButtonRef.current && !deleteButtonRef.current.contains(event.target as Node)) {
        setDeleteConfirmActive(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [deleteConfirmActive]);
  
  // 计算当前显示时间
  const currentTimeMs = mode === 'countdown' ? remainingMs : elapsedMs;
  const hours = Math.floor(currentTimeMs / (1000 * 60 * 60));
  let minutes = Math.floor((currentTimeMs % (1000 * 60 * 60)) / (1000 * 60));

  // 秒数计算逻辑：当不显示毫秒时使用向上取整，确保直观显示
  let seconds = showMilliseconds 
    ? Math.floor((currentTimeMs % (1000 * 60)) / 1000) 
    : Math.ceil((currentTimeMs % (1000 * 60)) / 1000);
  
  const milliseconds = currentTimeMs % 1000;

  // 特殊情况处理：如果向上取整后秒数为60，则应该进位到分钟
  if (!showMilliseconds && seconds === 60) {
    minutes += 1;
    seconds = 0;
  }
  
  // 计算进度百分比
  const progress = mode === 'countdown'
    ? initialTotalMs > 0 ? (remainingMs / initialTotalMs) * 100 : 0 // 倒计时：从100%到0%
    : (elapsedMs / (initialTotalMs || 3600000)) * 100; // 秒表：从0%到100%
  
  // 播放完成提示音
  const playCompletionSound = useCallback(() => {
    // 如果通知被禁用，则不播放声音
    if (!notificationsEnabled) return;
    
    try {
      // 使用正确的声音路径，基于设置中选择的通知声音
      const soundMap: Record<string, string> = {
        default: '/sounds/default.mp3',
        bell: '/sounds/bell.mp3',
        digital: '/sounds/digital.mp3',
        chime: '/sounds/chime.mp3',
        none: '' // 无声选项
      };
      
      // 使用用户设置的声音，如果没有设置或无效则回退到默认
      const soundKey = notificationSound && soundMap.hasOwnProperty(notificationSound) 
        ? notificationSound 
        : 'default';
      
      // 如果选择了'none'（无声），则不播放
      if (soundKey === 'none') return;
      
      const soundUrl = soundMap[soundKey];
      
      if (soundUrl) {
        const audio = new Audio(soundUrl);
        audio.volume = 0.7; // 设置适当的音量
        
        // 预加载音频确保能够播放
        audio.load();
        
        // 播放音频并处理可能的错误
        audio.play()
          .catch(error => {
            console.error('播放提示音失败:', error);
            // 如果指定声音加载失败，尝试使用默认声音
            if (soundKey !== 'default') {
              const fallbackAudio = new Audio(soundMap.default);
              fallbackAudio.volume = 0.7;
              fallbackAudio.play().catch(e => console.error('播放默认提示音也失败:', e));
            }
          });
      }
    } catch (error) {
      console.error('创建音频对象失败:', error);
    }
  }, [notificationSound, notificationsEnabled]);
  
  // 更新计时器的函数
  const updateTimer = useCallback((timestamp: number) => {
    if (!isRunningRef.current) return;
    
    if (lastTickTimeRef.current === null) {
      lastTickTimeRef.current = timestamp;
      animationFrameRef.current = requestAnimationFrame(updateTimer);
      return;
    }
    
    const deltaMs = timestamp - lastTickTimeRef.current;
    lastTickTimeRef.current = timestamp;
    
    if (mode === 'countdown') {
      // 倒计时模式
      const currentRemaining = remainingMsRef.current;
      
      // 避免负值，确保最小为0
      const newRemainingMs = Math.max(0, currentRemaining - deltaMs);
      
      // 计算当前显示秒数
      let currentDisplaySeconds;
      if (showMilliseconds) {
        // 显示毫秒时，直接向下取整
        currentDisplaySeconds = Math.floor((newRemainingMs % (1000 * 60)) / 1000);
      } else {
        // 不显示毫秒时，向上取整
        currentDisplaySeconds = Math.ceil((newRemainingMs % (1000 * 60)) / 1000);
      }
      
      // 当显示秒数为0且没有分钟和小时时，已经可以视为结束了
      const hoursPart = Math.floor(newRemainingMs / (1000 * 60 * 60));
      const minutesPart = Math.floor((newRemainingMs % (1000 * 60 * 60)) / (1000 * 60));
      const isEffectivelyZero = currentDisplaySeconds === 0 && minutesPart === 0 && hoursPart === 0;
      
      // 检查结束条件：
      // 1. 有毫秒时，newRemainingMs必须为0
      // 2. 无毫秒时，当显示为0秒且没有分和时时也算完成
      const isFinished = newRemainingMs === 0 || (!showMilliseconds && isEffectivelyZero);
      
      // 更新状态
      setRemainingMs(newRemainingMs);
      remainingMsRef.current = newRemainingMs;
      
      // 检查是否完成
      if (isFinished && currentRemaining > 0) {
        setIsRunning(false);
        isRunningRef.current = false;
        
        // 强制确保remainingMs为0
        setRemainingMs(0);
        remainingMsRef.current = 0;
        
        if (onComplete) onComplete();
        // 播放提示音
        playCompletionSound();
        return; // 停止继续请求动画帧
      }
    } else {
      // 秒表模式
      const currentElapsed = elapsedMsRef.current;
      const newElapsedMs = currentElapsed + deltaMs;
      
      setElapsedMs(newElapsedMs);
      elapsedMsRef.current = newElapsedMs;
    }
    
    animationFrameRef.current = requestAnimationFrame(updateTimer);
  }, [mode, onComplete, playCompletionSound, showMilliseconds]);
  
  // 管理动画帧
  useEffect(() => {
    if (isRunning) {
      lastTickTimeRef.current = null;
      animationFrameRef.current = requestAnimationFrame(updateTimer);
    } else if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isRunning, updateTimer]);
  
  // 开始计时器
  const startTimer = useCallback(() => {
    if (mode === 'countdown' && remainingMs === 0) {
      // 如果倒计时已结束，重置后再开始
      setRemainingMs(initialTotalMs);
      remainingMsRef.current = initialTotalMs;
    }
    
    setIsRunning(true);
  }, [mode, remainingMs, initialTotalMs]);
  
  // 暂停计时器
  const pauseTimer = useCallback(() => {
    setIsRunning(false);
  }, []);
  
  // 停止计时器
  const stopTimer = useCallback(() => {
    setIsRunning(false);
    
    // 重置计时器
    if (mode === 'countdown') {
      setRemainingMs(initialTotalMs);
      remainingMsRef.current = initialTotalMs;
    } else {
      setElapsedMs(0);
      elapsedMsRef.current = 0;
    }
  }, [mode, initialTotalMs]);
  
  // 重置计时器
  const resetTimer = useCallback(() => {
    if (mode === 'countdown') {
      setRemainingMs(initialTotalMs);
      remainingMsRef.current = initialTotalMs;
    } else {
      setElapsedMs(0);
      elapsedMsRef.current = 0;
    }
  }, [mode, initialTotalMs]);
  
  // 处理删除计时器
  const handleDelete = useCallback(() => {
    // 如果已经是确认状态，执行删除（传递confirmed=true以跳过额外确认）
    if (deleteConfirmActive) {
      if (onDelete) {
        onDelete(id, true);
      }
      setDeleteConfirmActive(false);
    } else {
      // 第一次点击，进入确认状态
      setDeleteConfirmActive(true);
    }
  }, [id, onDelete, deleteConfirmActive]);
  
  // 处理编辑计时器
  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);
  
  // 处理编辑提交
  const handleEditSubmit = useCallback((data: {
    name: string;
    hours: number;
    minutes: number;
    seconds: number;
    mode: TimerMode;
    showMilliseconds: boolean;
    effectType: EffectType;
    effectOpacity: number;
    effectColor?: string;
    effectSecondaryColor?: string;
    effectSpeed: number;
  }) => {
    if (onEdit) {
      onEdit(id, data);
    }
    setIsEditing(false);
  }, [id, onEdit]);
  
  // 切换全屏
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      // 进入全屏
      if (timerCardRef.current?.requestFullscreen) {
        timerCardRef.current.requestFullscreen()
          .then(() => {
            setIsFullscreen(true);
          })
          .catch(err => {
            console.error(`全屏错误: ${err.message}`);
          });
      }
    } else {
      // 退出全屏
      if (document.exitFullscreen) {
        document.exitFullscreen()
          .then(() => {
            setIsFullscreen(false);
          })
          .catch(err => {
            console.error(`退出全屏错误: ${err.message}`);
          });
      }
    }
  }, []);

  // 监听全屏状态变化
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);
  
  // 监听ESC键退出全屏
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        // 在某些浏览器中，ESC已经会触发退出全屏
        // 但我们需要确保状态也被正确更新
        setIsFullscreen(false);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);
  
  // 如果正在编辑，显示编辑表单
  if (isEditing && !isFullscreen) {
    return (
      <TimerForm
        onSubmit={handleEditSubmit}
        onCancel={() => setIsEditing(false)}
        initialValues={{
          name,
          hours: initialHours,
          minutes: initialMinutes,
          seconds: initialSeconds,
          mode,
          showMilliseconds,
          effectType,
          effectOpacity,
          effectColor,
          effectSecondaryColor,
          effectSpeed
        }}
        isEditing={true}
      />
    );
  }
  
  return (
    <motion.div
      ref={timerCardRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`timer-card ${className} ${isFullscreen ? 'fixed inset-0 flex flex-col justify-center items-center bg-gray-100 dark:bg-gray-900 z-50' : ''}`}
      style={{ pointerEvents: 'auto' }}
    >
      {isEditing && isFullscreen ? (
        <div className="absolute inset-0 flex items-center justify-center z-[80] bg-gray-100/95 dark:bg-gray-900/95">
          <div className="w-4/5 max-w-2xl mx-auto">
            <TimerForm
              onSubmit={handleEditSubmit}
              onCancel={() => setIsEditing(false)}
              initialValues={{
                name,
                hours: initialHours,
                minutes: initialMinutes,
                seconds: initialSeconds,
                mode,
                showMilliseconds,
                effectType,
                effectOpacity,
                effectColor,
                effectSecondaryColor,
                effectSpeed
              }}
              isEditing={true}
              inFullscreen={true}
            />
          </div>
        </div>
      ) : (
        <>
          <div className={`flex justify-between items-start mb-4 ${isFullscreen ? 'absolute top-4 left-4 right-4 z-[60]' : ''}`}
              style={{ pointerEvents: 'auto' }}>
            <h3 className="text-lg font-semibold truncate max-w-[70%]">{name}</h3>
            <div className="flex items-center space-x-2" style={{ pointerEvents: 'auto' }}>
              <span className="text-sm px-2 py-1 rounded-full bg-gray-200 dark:bg-gray-700">
                {mode === 'countdown' ? t('countdown') : t('stopwatch')}
              </span>
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-full text-gray-500 hover:text-primary hover:bg-gray-200 dark:hover:bg-gray-700 ${isFullscreen ? 'bg-gray-200 dark:bg-gray-700' : ''}`}
                aria-label={isFullscreen ? t('exitFullscreen') : t('fullscreen')}
                style={{ pointerEvents: 'auto', zIndex: 45 }}
              >
                {isFullscreen ? <FaCompress size={isFullscreen ? 20 : 16} /> : <FaExpand size={isFullscreen ? 20 : 16} />}
              </button>
              <button
                onClick={handleEdit}
                className="p-2 rounded-full text-gray-500 hover:text-primary hover:bg-gray-200 dark:hover:bg-gray-700"
                aria-label={t('edit')}
                style={{ pointerEvents: 'auto', zIndex: 45 }}
              >
                <FaEdit size={isFullscreen ? 20 : 16} />
              </button>
              <div className="relative" style={{ pointerEvents: 'auto', zIndex: 45 }}>
                <button
                  ref={deleteButtonRef}
                  onClick={handleDelete}
                  className={`p-2 rounded-full ${deleteConfirmActive ? 'text-danger animate-pulse bg-red-100 dark:bg-red-900/30' : 'text-gray-500 hover:text-danger hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                  aria-label={deleteConfirmActive ? t('confirm') + ' ' + t('delete') : t('delete')}
                >
                  <FaTrash size={isFullscreen ? 20 : 16} />
                </button>
                <AnimatePresence>
                  {deleteConfirmActive && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="absolute right-0 top-8 bg-danger text-white text-xs py-1 px-2 rounded whitespace-nowrap z-10 shadow-md"
                    >
                      {t('clickAgainToDelete')}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
          
          <div className={`circular-progress-container relative ${isFullscreen ? 'scale-150 my-auto' : 'mb-4'}`}>
            <TimerEffect
              effectType={effectType}
              mode={mode}
              totalMs={initialTotalMs}
              currentMs={mode === 'countdown' ? remainingMs : elapsedMs}
              showMilliseconds={showMilliseconds}
              size={isFullscreen ? 240 : 160}
              opacity={effectOpacity}
              color={effectColor || (mode === 'countdown' ? '#4A6FA5' : '#FF6B6B')}
              secondaryColor={effectSecondaryColor}
              speed={effectSpeed}
              isRunning={isRunning}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <TimerDisplay
                hours={hours}
                minutes={minutes}
                seconds={seconds}
                milliseconds={milliseconds}
                showMilliseconds={showMilliseconds}
                size={isFullscreen ? "xl" : "lg"}
                variant={mode === 'countdown' ? 'countdown' : 'elapsed'}
              />
            </div>
          </div>
          
          <div className={`timer-controls grid grid-cols-2 gap-2 sm:flex sm:space-x-2 ${isFullscreen ? 'absolute bottom-8 left-0 right-0 flex justify-center z-[60]' : ''}`}
               style={{ pointerEvents: 'auto' }}>
            {!isRunning ? (
              <Button 
                variant="primary" 
                icon={<FaPlay size={isFullscreen ? 20 : 16} />} 
                onClick={startTimer}
                aria-label={t('start')}
                className={`w-full sm:w-auto ${isFullscreen ? 'text-lg py-3 px-6' : ''}`}
              >
                {t('start')}
              </Button>
            ) : (
              <Button 
                variant="warning" 
                icon={<FaPause size={isFullscreen ? 20 : 16} />} 
                onClick={pauseTimer}
                aria-label={t('pause')}
                className={`w-full sm:w-auto ${isFullscreen ? 'text-lg py-3 px-6' : ''}`}
              >
                {t('pause')}
              </Button>
            )}
            
            <Button 
              variant="danger" 
              icon={<FaStop size={isFullscreen ? 20 : 16} />} 
              onClick={stopTimer}
              aria-label={t('stop')}
              className={`w-full sm:w-auto ${isFullscreen ? 'text-lg py-3 px-6' : ''}`}
            >
              {t('stop')}
            </Button>
            
            <Button 
              variant="outline" 
              icon={<FaRedo size={isFullscreen ? 20 : 16} />} 
              onClick={resetTimer}
              aria-label={t('reset')}
              className={`w-full sm:w-auto ${isFullscreen ? 'text-lg py-3 px-6' : ''}`}
            >
              {t('reset')}
            </Button>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default TimerCard;